import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  fetchMultiloc,
  createMultiloc,
  updateMultiloc,
  deleteMultiloc,
  fetchMultilocLines,
  applyMultilocToAll,
} from '@/api/multiloc'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import type {
  CreateMultilocRequest,
  UpdateMultilocRequest,
  MultilocLinesResponse,
} from '@/components/PlanDetail/MultilocDrawer/types'
import { useDebounce } from './useDebounce'
import { AxiosError } from 'axios'

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    const detail = error.response.data.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg ?? 'An error occurred'
    }
  }
  return 'An error occurred'
}

export function useMultiloc(
  planId: string | undefined,
  versionId: string | undefined,
  lineId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.multiloc.byLine(planId!, versionId!, lineId!),
    queryFn: () => fetchMultiloc(planId!, versionId!, lineId!),
    enabled: !!planId && !!versionId && !!lineId,
  })
}

export function useCreateMultiloc(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string
      body: CreateMultilocRequest
    }) => createMultiloc(planId, versionId, lineId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.multiloc.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      toast.success('Presentation model saved successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useUpdateMultiloc(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string
      body: UpdateMultilocRequest
    }) => updateMultiloc(planId, versionId, lineId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.multiloc.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      toast.success('Presentation model saved successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useDeleteMultiloc(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ lineId }: { lineId: string }) =>
      deleteMultiloc(planId, versionId, lineId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.multiloc.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      toast.success('Presentation model removed successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useMultilocLines(
  planId: string | undefined,
  versionId: string | undefined,
  search: string = ''
) {
  const debouncedSearch = useDebounce(search, 300)

  return useInfiniteQuery<MultilocLinesResponse>({
    queryKey: queryKeys.multiloc.lines(planId!, versionId!, debouncedSearch),
    queryFn: ({ pageParam = 0 }) =>
      fetchMultilocLines(planId!, versionId!, {
        offset: pageParam as number,
        limit: 20,
        q: debouncedSearch || undefined,
      }),
    getNextPageParam: lastPage =>
      lastPage.pagination.has_more
        ? lastPage.pagination.skip + lastPage.pagination.limit
        : undefined,
    initialPageParam: 0,
    enabled: !!planId && !!versionId,
    staleTime: 0,
    gcTime: 0,
  })
}

export function useApplyMultilocToAll(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (lineId: string) =>
      applyMultilocToAll(planId, versionId, lineId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.multiloc.allForPlan(planId, versionId),
      })
      toast.success('Presentation model applied to all line items')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}
